import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Volume2, VolumeX, Trash2, AlertTriangle, Lock, Check, LogOut, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const phoneConstraints = {
  '+91': { min: 10, max: 10 },
  '+1': { min: 10, max: 10 },
  '+44': { min: 10, max: 11 },
  '+61': { min: 9, max: 9 },
  '+971': { min: 9, max: 9 },
  '+49': { min: 10, max: 11 },
  '+33': { min: 9, max: 9 },
  '+39': { min: 9, max: 10 },
  '+34': { min: 9, max: 9 },
  '+65': { min: 8, max: 8 },
  '+60': { min: 9, max: 10 },
  '+81': { min: 10, max: 10 },
  '+86': { min: 11, max: 11 },
  '+27': { min: 9, max: 9 },
  '+64': { min: 8, max: 10 },
  '+55': { min: 10, max: 11 },
  '+52': { min: 10, max: 10 },
  '+31': { min: 9, max: 9 },
  '+41': { min: 9, max: 9 },
  '+46': { min: 7, max: 9 },
  '+972': { min: 8, max: 9 },
};

export default function Header({ activeSection, onNavigate, audioPlaying, onToggleAudio }) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteToast, setDeleteToast] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editCountryCode, setEditCountryCode] = useState('+91');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editToast, setEditToast] = useState(null);
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);

  const getErrorStr = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return fallback;
  };

  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    } else {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    }
    return () => {
      document.body.style.overflow = 'auto';
      if (window.lenis) window.lenis.start();
    };
  }, [showDeleteModal]);

  const handleDeleteAccount = async () => {
    const userEmail = localStorage.getItem('user_email');
    if (!userEmail || !deletePassword) {
      setDeleteError('Please enter your password to confirm.');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, password: deletePassword })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowDeleteModal(false);
        setDeletePassword('');
        localStorage.removeItem('user_authenticated');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        localStorage.removeItem('user_phone');
        localStorage.removeItem('user_role');
        setDeleteToast('Account deleted successfully. Redirecting...');
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        setDeleteError(getErrorStr(data.detail, 'Failed to delete account.'));
        setTimeout(() => setDeleteToast(null), 3000);
      }
    } catch (err) {
      setDeleteError('Network error. Please try again.');
      setDeleteLoading(false);
    }
  };
    
  const openEditProfile = () => {
    setShowProfileDropdown(false);
    setIsOpen(false);
    setEditName(userName || '');
    const p = userPhone || '';
    if (p.includes(' ')) {
      const parts = p.split(' ');
      setEditCountryCode(parts[0]);
      setEditPhone(parts.slice(1).join(' '));
    } else {
      setEditCountryCode('+91');
      setEditPhone(p);
    }
    setShowEditModal(true);
  };

  const handleEditProfile = async () => {
    const email = isUser ? localStorage.getItem('user_email') : (isAdmin ? localStorage.getItem('admin_email') : '');
    if (!email) return;
    setEditLoading(true);
    setEditError('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/auth/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: editName, phone: `${editCountryCode} ${editPhone}` })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowEditModal(false);
        if (isUser) {
          localStorage.setItem('user_name', data.name);
          localStorage.setItem('user_phone', data.phone);
        } else if (isAdmin) {
          localStorage.setItem('admin_name', data.name);
          localStorage.setItem('admin_phone', data.phone);
        }
        setEditToast('Profile updated successfully.');
        setTimeout(() => setEditToast(null), 3000);
      } else {
        setEditError(getErrorStr(data.detail, data.message || 'Update failed.'));
      }
    } catch (err) {
      setEditError('Network error. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideDesktop = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const clickedOutsideMobile = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target);
      
      if (clickedOutsideDesktop && clickedOutsideMobile) {
        setShowProfileDropdown(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isUser = localStorage.getItem('user_authenticated') === 'true';
  const isAdmin = localStorage.getItem('admin_authenticated') === 'true';
  const isLoggedIn = isUser || isAdmin;

  const userEmail = isUser ? localStorage.getItem('user_email') : (isAdmin ? localStorage.getItem('admin_email') : '');
  const userName = isUser ? localStorage.getItem('user_name') : (isAdmin ? localStorage.getItem('admin_name') : '');
  const userPhone = isUser ? localStorage.getItem('user_phone') : (isAdmin ? localStorage.getItem('admin_phone') : '');
  const userRole = isUser ? (localStorage.getItem('user_role') || 'user') : (isAdmin ? 'admin' : '');

  const navItems = [
    { id: 'hero', path: '/', label: t('header.navHome') },
    { id: 'destinations', path: '/sanctuary', label: t('header.navSanctuary') },
    { id: 'experiences', path: '/experiences', label: t('header.navExperiences') },
    { id: 'planner', path: '/planner', label: t('header.navPlanner') },
    { id: 'booking', path: '/retreats', label: t('header.navRetreats') },
    { id: 'aiguide', path: '/guide', label: t('header.navConsult', { defaultValue: 'Darshan' }) },
    { id: 'testimonials', path: '/stories', label: t('header.navStories') }
  ];

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300" style={{ transition: 'var(--transition-smooth)', padding: '16px 0', backgroundColor: '#FEF8F1', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.06)' : 'none' }}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center header-mobile-container" style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '90%', margin: '0 auto' }}>
        <div className="header-logo-container" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Link
            to="/"
            onClick={() => setIsOpen(false)}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <img
              className="header-logo-img"
              src="/images/new-logo.png"
              alt="Secondary Spiritual Logo"
              style={{
                height: '50px',
                objectFit: 'contain',
                transition: 'var(--transition-fast)'
              }}
            />
          </Link>

          <Link
            to="/"
            onClick={() => setIsOpen(false)}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <img
              className="header-logo-img"
              src="/images/logo.png"
              alt="MysticRoutes Spiritual Tourism Platform"
              style={{
                height: '50px',
                objectFit: 'contain',
                transition: 'var(--transition-fast)'
              }}
            />
          </Link>
        </div>

        <div className="md-flex-container" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', alignItems: 'center', gap: '1.25rem' }}>
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className="nav-link text-xs uppercase tracking-widest font-medium transition-colors"
              style={{
                textDecoration: 'none',
                fontSize: '0.72rem',
                letterSpacing: '0.12em',
                color: activeSection === item.id
                  ? 'var(--primary-gold)'
                  : 'var(--text-light)',
                position: 'relative',
                padding: '6px 0',
                whiteSpace: 'nowrap'
              }}
            >
              {item.label}
              {activeSection === item.id && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '2px',
                    backgroundColor: 'var(--primary-gold)',
                    animation: 'fadeIn 0.3s ease',
                    borderRadius: '1px'
                  }}
                />
              )}
            </Link>
          ))}
        </div>

        <div style={{ gap: '1.5rem', alignItems: 'center' }} className="md-flex-container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ position: 'relative' }} ref={langDropdownRef}>
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="btn-outline"
                style={{
                  padding: '8px 14px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.7rem',
                  letterSpacing: '0.1em',
                  color: 'var(--primary-gold)',
                  borderColor: 'var(--primary-gold)',
                  backgroundColor: 'transparent',
                  textTransform: 'uppercase'
                }}
              >
                <Globe size={14} /> {(() => {
                  const currentLang = i18n.language ? i18n.language.substring(0, 2).toLowerCase() : 'en';
                  const langMap = { en: 'English', fr: 'Français', de: 'Deutsch', ja: '日本語', it: 'Italiano', es: 'Español' };
                  return langMap[currentLang] || 'English';
                })()}
              </button>

              {showLangDropdown && (
                <>
                  <div
                    onClick={() => setShowLangDropdown(false)}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(5px)',
                      zIndex: 999,
                      animation: 'fadeIn 0.2s ease-out',
                      cursor: 'default'
                    }}
                  />
                  <div
                    className="glass-card"
                    style={{
                      position: 'absolute',
                      top: '40px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '120px',
                      padding: '8px 0',
                      zIndex: 1000,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                  {Object.entries({ en: 'English', fr: 'Français', de: 'Deutsch', ja: '日本語', it: 'Italiano', es: 'Español' }).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => { i18n.changeLanguage(code).then(() => window.location.reload()); setShowLangDropdown(false); }}
                      style={{
                        padding: '8px 16px',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-light)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)'}
                      onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {name} {i18n.language.startsWith(code) && <Check size={12} className="text-gold" />}
                    </button>
                  ))}
                  </div>
                </>
              )}
            </div>

            {isLoggedIn ? (
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--primary-gold) 0%, #b89225 100%)',
                    border: '1px solid var(--primary-gold-border)',
                    color: 'var(--text-dark)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  title="View Profile"
                >
                  {userName ? userName.charAt(0).toUpperCase() : (userEmail ? userEmail.charAt(0).toUpperCase() : 'U')}
                </button>

                {showProfileDropdown && (
                  <div
                    className="glass-card"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '48px',
                      width: '280px',
                      padding: '20px',
                      zIndex: 1000,
                      animation: 'fadeIn 0.2s ease-out',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', paddingBottom: '12px' }}>
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          background: 'var(--primary-gold-trans)',
                          border: '1px solid var(--primary-gold-border)',
                          color: 'var(--primary-gold)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                          fontSize: '1.1rem'
                        }}
                      >
                        {userName ? userName.charAt(0).toUpperCase() : (userEmail ? userEmail.charAt(0).toUpperCase() : 'U')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left' }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: '600', color: 'var(--text-light)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={userName || 'Mystic Routes'}>
                          {userName || 'Mystic Routes'}
                        </span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--primary-saffron)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                          {userRole === 'admin' ? 'Administrator' : 'Seeker'}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'left' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Email</span>
                        <span style={{ color: 'var(--text-light)', wordBreak: 'break-all' }} title={userEmail}>{userEmail}</span>
                      </div>
                      {userPhone && (
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '4px' }}>
                          <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Phone</span>
                          <span style={{ color: 'var(--text-light)' }}>{userPhone}</span>
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: '1px solid rgba(181, 137, 37, 0.15)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {!isAdmin && (
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            window.location.href = '/dashboard';
                          }}
                          className="btn-gold"
                          style={{
                            padding: '8px',
                            textAlign: 'center',
                            borderRadius: '4px',
                            fontSize: '0.72rem',
                            letterSpacing: '0.1em',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            display: 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          My Dashboard
                        </button>
                      )}
                      
                      <button
                        onClick={openEditProfile}
                        className="btn-outline"
                        style={{
                          padding: '8px',
                          textAlign: 'center',
                          borderRadius: '4px',
                          fontSize: '0.72rem',
                          letterSpacing: '0.1em',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          display: 'block',
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid rgba(181, 137, 37, 0.25)',
                          cursor: 'pointer',
                          marginBottom: '8px'
                        }}
                      >
                        Edit Profile
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            window.location.href = '/admin';
                          }}
                          className="btn-outline"
                          style={{
                            padding: '8px',
                            textAlign: 'center',
                            borderRadius: '4px',
                            fontSize: '0.72rem',
                            letterSpacing: '0.1em',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            display: 'block',
                            width: '100%',
                            boxSizing: 'border-box',
                            border: '1px solid var(--primary-gold-border)',
                            cursor: 'pointer',
                            background: 'transparent'
                          }}
                        >
                          {t('header.btnAdmin')}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          if (isAdmin) {
                            localStorage.removeItem('admin_authenticated');
                            localStorage.removeItem('admin_email');
                            localStorage.removeItem('admin_name');
                            localStorage.removeItem('admin_phone');
                          } else {
                            localStorage.removeItem('user_authenticated');
                            localStorage.removeItem('user_email');
                            localStorage.removeItem('user_name');
                            localStorage.removeItem('user_phone');
                            localStorage.removeItem('user_role');
                          }
                          window.location.href = '/';
                        }}
                        className="btn-outline"
                        style={{
                          padding: '8px',
                          borderRadius: '4px',
                          fontSize: '0.72rem',
                          letterSpacing: '0.1em',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          background: 'none',
                          border: '1px solid rgba(181, 137, 37, 0.25)',
                          color: 'var(--text-light)',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <LogOut size={12} /> {t('header.btnLogout')}
                      </button>
                      {!isAdmin && (
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            setShowDeleteModal(true);
                          }}
                          className="btn-outline"
                          style={{
                            padding: '8px',
                            borderRadius: '4px',
                            fontSize: '0.72rem',
                            letterSpacing: '0.1em',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            background: 'none',
                            border: '1px solid rgba(198, 40, 40, 0.3)',
                            color: 'var(--accent-terracotta)',
                            width: '100%'
                          }}
                          title="Permanently delete your account"
                        >
                          <Trash2 size={12} /> {t('header.btnDelete')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-outline"
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  color: 'var(--primary-gold)',
                  borderColor: 'var(--primary-gold)',
                  backgroundColor: 'transparent'
                }}
              >
                {t('header.btnLogin')}
              </Link>
            )}
          </div>
        </div>

        <div style={{ alignItems: 'center', gap: '1rem' }} className="md-hidden-toggle header-controls-container">
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowLangDropdown(!showLangDropdown); }}
              style={{
                background: 'none',
                border: '1px solid',
                borderColor: 'var(--primary-gold)',
                color: 'var(--primary-gold)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '0.7rem',
                textTransform: 'uppercase'
              }}
            >
              {i18n.language ? i18n.language.substring(0, 2) : 'EN'}
            </button>
            
            {showLangDropdown && (
              <>
                <div
                  onClick={() => setShowLangDropdown(false)}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(5px)',
                    zIndex: 999
                  }}
                />
                <div
                  className="glass-card"
                  style={{
                    position: 'absolute',
                    top: '35px',
                    right: 0,
                    width: '120px',
                    padding: '8px 0',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {Object.entries({ en: 'English', fr: 'Français', de: 'Deutsch', ja: '日本語', it: 'Italiano', es: 'Español' }).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => { i18n.changeLanguage(code).then(() => window.location.reload()); setShowLangDropdown(false); }}
                      style={{
                        padding: '8px 16px',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-light)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        letterSpacing: '0.05em',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)'}
                      onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {name} {i18n.language.startsWith(code) && <Check size={12} className="text-gold" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {isLoggedIn && (
            <div style={{ position: 'relative' }} ref={mobileDropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--primary-gold) 0%, #b89225 100%)',
                  border: '1px solid var(--primary-gold-border)',
                  color: 'var(--text-dark)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                title="View Profile"
              >
                {userName ? userName.charAt(0).toUpperCase() : (userEmail ? userEmail.charAt(0).toUpperCase() : 'U')}
              </button>

              {showProfileDropdown && (
                <div
                  className="glass-card"
                  style={{
                    position: 'absolute',
                    right: '-40px',
                    top: '44px',
                    width: '260px',
                    padding: '16px',
                    zIndex: 1000,
                    animation: 'fadeIn 0.2s ease-out',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', paddingBottom: '10px' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: 'var(--primary-gold-trans)',
                        border: '1px solid var(--primary-gold-border)',
                        color: 'var(--primary-gold)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '0.9rem'
                      }}
                    >
                      {userName ? userName.charAt(0).toUpperCase() : (userEmail ? userEmail.charAt(0).toUpperCase() : 'U')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--text-light)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {userName || 'Mystic Routes'}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--primary-saffron)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                        {userRole === 'admin' ? 'Administrator' : 'Seeker'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'left' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Email</span>
                      <span style={{ color: 'var(--text-light)', wordBreak: 'break-all' }}>{userEmail}</span>
                    </div>
                    {userPhone && (
                      <div style={{ display: 'flex', flexDirection: 'column', marginTop: '2px' }}>
                        <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Phone</span>
                        <span style={{ color: 'var(--text-light)' }}>{userPhone}</span>
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid rgba(181, 137, 37, 0.15)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <button
                      onClick={() => {
                        openEditProfile();
                      }}
                      className="btn-outline"
                      style={{
                        padding: '6px',
                        textAlign: 'center',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        display: 'block',
                        width: '100%',
                        boxSizing: 'border-box',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        cursor: 'pointer'
                      }}
                    >
                      Edit Profile
                    </button>
                    {!isAdmin && (
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          window.location.href = '/dashboard';
                        }}
                        className="btn-gold"
                        style={{
                          padding: '6px',
                          textAlign: 'center',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          letterSpacing: '0.1em',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          display: 'block',
                          width: '100%',
                          boxSizing: 'border-box',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        My Dashboard
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          window.location.href = '/admin';
                        }}
                        className="btn-outline"
                        style={{
                          padding: '6px',
                          textAlign: 'center',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          letterSpacing: '0.1em',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          display: 'block',
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid var(--primary-gold-border)',
                          cursor: 'pointer',
                          background: 'transparent'
                        }}
                      >
                        {t('header.btnAdmin')}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        if (isAdmin) {
                          localStorage.removeItem('admin_authenticated');
                          localStorage.removeItem('admin_email');
                          localStorage.removeItem('admin_name');
                          localStorage.removeItem('admin_phone');
                        } else {
                          localStorage.removeItem('user_authenticated');
                          localStorage.removeItem('user_email');
                          localStorage.removeItem('user_name');
                          localStorage.removeItem('user_phone');
                          localStorage.removeItem('user_role');
                        }
                        window.location.href = '/';
                      }}
                      className="btn-outline"
                      style={{
                        padding: '6px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        background: 'none',
                        border: '1px solid rgba(181, 137, 37, 0.25)',
                        color: 'var(--text-light)',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <LogOut size={12} /> {t('header.btnLogout')}
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        setShowDeleteModal(true);
                      }}
                      className="btn-outline"
                      style={{
                        padding: '6px',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        background: 'none',
                        border: '1px solid rgba(198, 40, 40, 0.3)',
                        color: 'var(--accent-terracotta)',
                        width: '100%'
                      }}
                    >
                      <Trash2 size={12} /> {t('header.btnDelete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => setIsOpen(!isOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-light)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className="glass-card"
          style={{
            position: 'fixed',
            top: '80px',
            left: '5%',
            width: '90%',
            padding: '2rem',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => setIsOpen(false)}
              style={{
                textDecoration: 'none',
                textAlign: 'left',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: activeSection === item.id ? 'var(--primary-gold)' : 'var(--text-light)',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}
            >
              {item.label}
            </Link>
          ))}

          {isLoggedIn ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px', padding: '15px', borderRadius: '8px', border: '1px solid rgba(181, 137, 37, 0.15)', background: 'rgba(255, 255, 255, 0.25)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid rgba(181, 137, 37, 0.15)', paddingBottom: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-gold-trans)',
                    border: '1px solid var(--primary-gold-border)',
                    color: 'var(--primary-gold)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '1rem'
                  }}
                >
                  {userName ? userName.charAt(0).toUpperCase() : (userEmail ? userEmail.charAt(0).toUpperCase() : 'U')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-light)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userName || 'Mystic Routes'}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: 'var(--primary-saffron)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                    {userRole === 'admin' ? 'Administrator' : 'Seeker'}
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'left' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Email</span>
                  <span style={{ color: 'var(--text-light)', wordBreak: 'break-all' }}>{userEmail}</span>
                </div>
                {userPhone && (
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '4px' }}>
                    <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', opacity: 0.8 }}>Phone</span>
                    <span style={{ color: 'var(--text-light)' }}>{userPhone}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                <button
                  onClick={() => {
                    openEditProfile();
                  }}
                  className="btn-outline"
                  style={{
                    padding: '10px',
                    textAlign: 'center',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    letterSpacing: '0.1em',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'block',
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1px solid rgba(181, 137, 37, 0.25)',
                    cursor: 'pointer'
                  }}
                >
                  Edit Profile
                </button>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsOpen(false)}
                    className="btn-outline"
                    style={{
                      padding: '10px',
                      textAlign: 'center',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      letterSpacing: '0.1em',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      display: 'block',
                      width: '100%',
                      boxSizing: 'border-box',
                      borderColor: 'var(--primary-gold)',
                      color: 'var(--primary-gold)'
                    }}
                  >
                    {t('header.btnAdmin')}
                  </Link>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    if (isAdmin) {
                      localStorage.removeItem('admin_authenticated');
                      localStorage.removeItem('admin_email');
                      localStorage.removeItem('admin_name');
                      localStorage.removeItem('admin_phone');
                    } else {
                      localStorage.removeItem('user_authenticated');
                      localStorage.removeItem('user_email');
                      localStorage.removeItem('user_name');
                      localStorage.removeItem('user_phone');
                      localStorage.removeItem('user_role');
                    }
                    window.location.href = '/';
                  }}
                  className="btn-outline"
                  style={{
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    letterSpacing: '0.1em',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    background: 'none',
                    border: '1px solid rgba(181, 137, 37, 0.25)',
                    color: 'var(--text-light)',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <LogOut size={14} /> {t('header.btnLogout')}
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowDeleteModal(true);
                  }}
                  className="btn-outline"
                  style={{
                    padding: '10px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    letterSpacing: '0.1em',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: '100%',
                    cursor: 'pointer',
                    background: 'none',
                    border: '1px solid rgba(198, 40, 40, 0.3)',
                    color: 'var(--accent-terracotta)'
                  }}
                >
                  <Trash2 size={14} /> {t('header.btnDelete')}
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="btn-outline"
              style={{
                padding: '12px',
                textAlign: 'center',
                borderRadius: '4px',
                fontSize: '0.85rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                display: 'block',
                marginTop: '10px'
              }}
            >
              {t('header.btnLogin')}
            </Link>
          )}
        </div>
      )}

      <style>{`
        .nav-link {
          transition: color 0.3s ease, transform 0.2s ease;
        }
        .nav-link:hover {
          color: var(--primary-gold) !important;
          transform: translateY(-1px);
        }
        @media (min-width: 1150px) {
          .md-flex-container {
            display: flex !important;
          }
          .md-hidden-toggle {
            display: none !important;
          }
        }
      `}</style>

      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(7, 5, 4, 0.5)',
            backdropFilter: 'blur(6px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
            setShowDeleteModal(false);
            setDeletePassword('');
            setDeleteError('');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            style={{
              width: '100%',
              maxWidth: '420px',
              padding: '35px 30px',
              backgroundColor: 'var(--bg-deep)',
              borderRadius: '16px',
              border: '1px solid rgba(198, 40, 40, 0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              animation: 'fadeIn 0.3s ease-out',
              position: 'relative'
            }}
          >
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeletePassword('');
                setDeleteError('');
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(198, 40, 40, 0.08)',
                  border: '1px solid rgba(198, 40, 40, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 15px',
                  color: 'var(--accent-terracotta)'
                }}
              >
                <AlertTriangle size={28} />
              </div>
              <h3 style={{ fontSize: '1.4rem', color: 'var(--text-light)', margin: '0 0 8px 0', fontFamily: 'var(--font-serif)' }}>
                Delete Your Account?
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
                This action is <strong style={{ color: 'var(--accent-terracotta)' }}>permanent and irreversible</strong>.
                All your data will be removed from our system. Please enter your password to confirm.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '16px', top: '13px', color: 'var(--accent-terracotta)' }}>
                  <Lock size={18} />
                </span>
                <input
                  type="password"
                  placeholder="Enter your password to confirm"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleDeleteAccount(); }}
                  style={{
                    width: '100%',
                    padding: '12px 18px 12px 48px',
                    background: 'rgba(255, 255, 255, 0.75)',
                    border: deleteError ? '1px solid var(--accent-terracotta)' : '1px solid rgba(198, 40, 40, 0.2)',
                    borderRadius: '30px',
                    color: 'var(--text-light)',
                    outline: 'none',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              {deleteError && (
                <p style={{ color: 'var(--accent-terracotta)', fontSize: '0.78rem', fontWeight: '500', margin: '8px 0 0 16px' }}>
                  {deleteError}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteError('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '30px',
                  fontSize: '0.82rem',
                  fontWeight: '600',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  background: 'none',
                  border: '1px solid rgba(181, 137, 37, 0.25)',
                  color: 'var(--text-light)'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || !deletePassword}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '30px',
                  fontSize: '0.82rem',
                  fontWeight: '700',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: deleteLoading || !deletePassword ? 'not-allowed' : 'pointer',
                  background: 'var(--accent-terracotta)',
                  border: 'none',
                  color: '#fff',
                  opacity: deleteLoading || !deletePassword ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 size={14} />
                {deleteLoading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="glass-card animate-slide-up" style={{
            background: 'linear-gradient(145deg, #ffffff 0%, #FAF6F0 100%)',
            padding: '40px', borderRadius: '24px', width: '90%', maxWidth: '400px',
            position: 'relative', border: '1px solid rgba(214,179,106,0.5)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <button
              onClick={() => setShowEditModal(false)}
              style={{
                position: 'absolute', top: '20px', right: '20px',
                background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer'
              }}
            >
              <X size={24} />
            </button>
            <h2 className="font-serif" style={{ fontSize: '1.8rem', color: '#d4af37', marginBottom: '10px' }}>Edit Profile</h2>
            <p style={{ color: '#706152', fontSize: '0.9rem', marginBottom: '20px' }}>Update your personal information.</p>

            {editError && (
              <div style={{ backgroundColor: 'rgba(198, 40, 40, 0.1)', color: 'var(--accent-terracotta)', padding: '10px', borderRadius: '8px', fontSize: '0.8rem', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={14} /> {editError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--primary-gold)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value.replace(/[^A-Za-z\s]/g, ''))}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(214,179,106,0.3)', background: 'rgba(255,255,255,0.8)', color: '#2b2520', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--primary-gold)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select 
                    value={editCountryCode}
                    onChange={(e) => setEditCountryCode(e.target.value)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(214,179,106,0.3)',
                      background: 'rgba(255,255,255,0.8)',
                      color: '#2b2520',
                      outline: 'none',
                      width: '100px'
                    }}
                  >
                    <option value="+91">+91 (IN)</option>
                    <option value="+1">+1 (US/CA)</option>
                    <option value="+44">+44 (UK)</option>
                    <option value="+61">+61 (AU)</option>
                    <option value="+971">+971 (AE)</option>
                    <option value="+49">+49 (DE)</option>
                    <option value="+33">+33 (FR)</option>
                    <option value="+39">+39 (IT)</option>
                    <option value="+34">+34 (ES)</option>
                    <option value="+65">+65 (SG)</option>
                    <option value="+60">+60 (MY)</option>
                    <option value="+81">+81 (JP)</option>
                    <option value="+86">+86 (CN)</option>
                    <option value="+27">+27 (ZA)</option>
                    <option value="+64">+64 (NZ)</option>
                    <option value="+55">+55 (BR)</option>
                    <option value="+52">+52 (MX)</option>
                    <option value="+31">+31 (NL)</option>
                    <option value="+41">+41 (CH)</option>
                    <option value="+46">+46 (SE)</option>
                    <option value="+972">+972 (IL)</option>
                  </select>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      const maxLen = phoneConstraints[editCountryCode]?.max || 15;
                      if (v.length <= maxLen) setEditPhone(v);
                    }}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(214,179,106,0.3)', background: 'rgba(255,255,255,0.8)', color: '#2b2520', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--text-muted)', background: 'transparent', color: 'var(--text-muted)', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditProfile}
                  disabled={editLoading}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: 'var(--primary-gold)', color: '#fff', fontWeight: 600, cursor: editLoading ? 'wait' : 'pointer', opacity: editLoading ? 0.7 : 1 }}
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {deleteToast && (
        <div style={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: 'rgba(46, 125, 50, 0.95)', color: 'white', padding: '12px 24px',
          borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '10px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)', zIndex: 9999,
          animation: 'slideUpFade 0.3s ease-out forwards'
        }}>
          <Check size={18} />
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{deleteToast}</span>
        </div>
      )}
      {editToast && (
        <div style={{
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: 'rgba(46, 125, 50, 0.95)', color: 'white', padding: '12px 24px',
          borderRadius: '30px', display: 'flex', alignItems: 'center', gap: '10px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)', zIndex: 9999,
          animation: 'slideUpFade 0.3s ease-out forwards'
        }}>
          <Check size={18} />
          <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{editToast}</span>
        </div>
      )}
    </nav>
  );
}
